import { useState } from "react";
import { Link, useParams } from "react-router";
import { motion } from "motion/react";
import { MapPin, Calendar, TrendingUp, ArrowLeft, CheckCircle2, Users, Target } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { DonationWidget } from "../components/donation-widget";
import { projects } from "../data/projects";

const sdgVisuals: Record<number, { src: string; alt: string; label: string }> = {
  8: {
    src: "/sdg8.png",
    alt: "Sustainable Development Goal 8",
    label: "SDG 8",
  },
  10: {
    src: "/sdg10.png",
    alt: "Sustainable Development Goal 10",
    label: "SDG 10",
  },
  12: {
    src: "/sdg12.png",
    alt: "Sustainable Development Goal 12",
    label: "SDG 12",
  },
};

export function ProjectDetail() {
  const { id } = useParams();
  const project = projects.find((p) => p.id === id);
  const [isDonationWidgetOpen, setIsDonationWidgetOpen] = useState(false);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl mb-4">Project Not Found</h1>
          <Link to="/projects">
            <Button>Back to Projects</Button>
          </Link>
        </div>
      </div>
    );
  }

  const progress = project.goal > 0 ? (project.raised / project.goal) * 100 : project.status === "Completed" ? 100 : 0;
  const remaining = Math.max(project.goal - project.raised, 0);
  const impactTiles = project.unwtoGoals
    .map((goal) => ({ goal, ...sdgVisuals[goal] }))
    .filter((tile) => tile.src);

  return (
    <div>
      {/* Full-Width Hero */}
      <section className="relative h-[70vh] flex items-end">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.2 }}
          className="absolute inset-0"
        >
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        </motion.div>

        <div className="relative z-10 w-full py-16 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/projects">
              <Button
                variant="ghost"
                size="sm"
                className="mb-8 text-white hover:bg-white/10 backdrop-blur-sm"
              >
                <ArrowLeft className="mr-2" size={18} />
                Back to Projects
              </Button>
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="max-w-4xl"
            >
              <div className="flex items-center gap-3 mb-6">
                <Badge
                  className={
                    project.status === "Active"
                      ? "bg-secondary text-secondary-foreground"
                      : "bg-accent text-accent-foreground"
                  }
                >
                  {project.status}
                </Badge>
                <Badge variant="outline" className="bg-white/10 text-white border-white/30 backdrop-blur-sm">
                  {project.category}
                </Badge>
              </div>

              <h1 className="text-5xl md:text-7xl mb-6 leading-tight font-bold">
                {project.title}
              </h1>

              <div className="flex items-center gap-3 text-xl mb-8">
                <MapPin size={24} />
                <span>{project.location}</span>
              </div>

              <p className="text-2xl leading-relaxed max-w-3xl opacity-95">
                {project.description}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Funding Tracker - Prominent */}
      <section className="bg-white py-16 border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-center"
          >
            <div className="mb-8">
              <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 mb-8">
                <div>
                  <div className="text-6xl md:text-7xl font-bold text-primary mb-2">
                    {project.hideFundingAmounts ? project.raisedLabel || "Self Funded" : `₹${(project.raised / 100000).toFixed(1)}L`}
                  </div>
                  <div className="text-lg text-muted-foreground">raised</div>
                </div>
                <div className="text-4xl text-muted-foreground hidden md:block">
                  /
                </div>
                <div>
                  <div className="text-5xl md:text-6xl font-bold text-foreground mb-2">
                    {project.hideFundingAmounts ? project.goalLabel || "Goal Achieved" : `₹${(project.goal / 100000).toFixed(1)}L`}
                  </div>
                  <div className="text-lg text-muted-foreground">goal</div>
                </div>
              </div>

              {/* Animated Progress Bar */}
              <div className="relative h-4 bg-muted rounded-full overflow-hidden mb-4">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 1.5, ease: "easeOut", delay: 0.7 }}
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-secondary rounded-full"
                />
              </div>

              <div className="flex items-center justify-between text-sm text-muted-foreground mb-8">
                <span>{project.hideFundingAmounts ? project.completionLabel || "100% completed" : `${progress.toFixed(0)}% funded`}</span>
                <span>{project.hideFundingAmounts ? "Project Achieved" : `₹${(remaining / 100000).toFixed(1)}L to go`}</span>
              </div>

              {project.status === "Active" && (
                <Button
                  size="lg"
                  onClick={() => setIsDonationWidgetOpen(true)}
                  className="text-xl px-12 py-8 shadow-lg hover:shadow-xl transition-shadow"
                >
                  Support This Project
                </Button>
              )}

              {project.status === "Completed" && (
                <div className="inline-flex items-center gap-3 px-8 py-4 bg-accent/20 rounded-full text-accent-foreground">
                  <CheckCircle2 size={24} />
                  <span className="text-lg font-semibold">
                    Project Successfully Completed
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-16">

            {/* UNWTO Goals */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="text-4xl mb-6">Impact Alignment</h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                This project aligns with UN Sustainable Development and UNWTO
                Goals for sustainable tourism and community development.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                {impactTiles.map(({ goal, src, alt, label }) => (
                  <div key={goal} className="space-y-3">
                    <div className="overflow-hidden rounded-2xl">
                      <img
                        src={src}
                        alt={alt}
                        className="aspect-square w-24 h-24 object-contain p-2 mx-auto"
                        loading="lazy"
                      />
                    </div>
                    <div className="text-sm font-semibold text-muted-foreground text-center">
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Partner Organization */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-muted/30 rounded-2xl p-8"
            >
              <h2 className="text-4xl mb-6">Grassroots Partner</h2>
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Users className="text-primary" size={32} />
                </div>
                <div>
                  <h3 className="text-2xl mb-2">Verified Grassroots Partner</h3>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    A verified grassroots organization with proven track record in
                    sustainable development and community empowerment across rural
                    India.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-primary">
                <CheckCircle2 size={20} />
                <span className="font-semibold">Verified Partner</span>
              </div>
            </motion.div>

            {/* Updates Timeline */}
            {project.updates && project.updates.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <h2 className="text-4xl mb-8">Timeline & Updates</h2>
                <div className="relative space-y-8">
                  {/* Timeline Line */}
                  <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-secondary to-muted" />

                  {project.updates.map((update, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="relative pl-20"
                    >
                      {/* Timeline Dot */}
                      <div className="absolute left-0 top-0 w-16 h-16 rounded-full bg-primary/10 border-4 border-white flex items-center justify-center z-10">
                        <CheckCircle2 className="text-primary" size={24} />
                      </div>

                      <div className="bg-white rounded-xl p-8 border border-border shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                          <Calendar size={16} />
                          <span>
                            {new Date(update.date).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </span>
                        </div>
                        <h3 className="text-2xl mb-3">{update.title}</h3>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                          {update.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          {/* Sidebar - Quick Info */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="sticky top-32"
            >
              <div className="bg-muted/30 rounded-2xl p-8 space-y-6 border border-border">
                <h3 className="text-2xl mb-6">Project Details</h3>

                {/* Quick Facts */}
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-primary" size={20} />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">
                        Location
                      </div>
                      <div className="font-semibold">{project.location}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Target className="text-primary" size={20} />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">
                        Category
                      </div>
                      <div className="font-semibold">{project.category}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="text-primary" size={20} />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">
                        Status
                      </div>
                      <div className="font-semibold">{project.status}</div>
                    </div>
                  </div>
                </div>

                {/* Share */}
                <div className="pt-6 border-t border-border">
                  <div className="text-sm text-muted-foreground mb-3">
                    Share this project
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1">
                      Share
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Donation Widget */}
      <DonationWidget
        isOpen={isDonationWidgetOpen}
        onClose={() => setIsDonationWidgetOpen(false)}
      />
    </div>
  );
}
